import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { Warning, CheckCircle } from '@mui/icons-material';
import { useNotify, useRefresh, useRedirect } from 'react-admin';
import { apiClient } from '../../utils/apiClient';

interface ForceCompleteDialogProps {
  open: boolean;
  onClose: () => void;
  session: {
    id: string;
    appSessionId: string;
    chargerName: string;
    siteName: string;
    status: string;
    energyDelivered: number;
  } | null;
}

interface ForceCompleteReason {
  value: string;
  label: string;
  description: string;
}

const FORCE_COMPLETE_REASONS: ForceCompleteReason[] = [
  {
    value: 'network_outage',
    label: 'Network Outage',
    description: 'Site network is down, charger cannot communicate'
  },
  {
    value: 'charger_hardware_failure',
    label: 'Charger Hardware Failure',
    description: 'Charger hardware malfunction preventing normal completion'
  },
  {
    value: 'site_power_outage',
    label: 'Site Power Outage',
    description: 'Power outage at the charging site'
  },
  {
    value: 'emergency_safety',
    label: 'Emergency/Safety',
    description: 'Emergency situation requiring immediate session termination'
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Other reason (please provide details in notes)'
  }
];

export const ForceCompleteDialog: React.FC<ForceCompleteDialogProps> = ({
  open,
  onClose,
  session
}) => {
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setNotes('');
      setError(null);
      onClose();
    }
  };

  const handleForceComplete = async () => {
    if (!session || !reason) {
      setError('Please select a reason');
      return;
    }

    if (reason === 'other' && !notes.trim()) {
      setError('Please provide details in notes when selecting "Other"');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        reason,
      };

      // Only include notes if provided
      if (notes.trim()) {
        payload.notes = notes.trim();
      }

      const data = await apiClient.post(
        `/api/backend/active-sessions/${session.appSessionId}/force-complete`,
        payload
      );

      notify('Session force-complete initiated successfully. Session will complete in 3 seconds.', {
        type: 'success'
      });

      // Wait a moment then refresh and redirect
      setTimeout(() => {
        refresh();
        redirect('/active-sessions');
      }, 1000);

      handleClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to force-complete session';
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!session) return null;

  const selectedReason = FORCE_COMPLETE_REASONS.find(r => r.value === reason);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          <Typography variant="h6" component="span">
            Force Complete Charging Session
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Use this only when the charger is offline or unreachable
          </Typography>
          <Typography variant="body2">
            This will manually complete the session using the last known data.
            The charger will be prevented from updating this session if it reconnects.
          </Typography>
        </Alert>

        <Box sx={{ mb: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
          <Typography variant="caption" color="textSecondary">Session Details</Typography>
          <Typography variant="body2"><strong>Session:</strong> {session.appSessionId}</Typography>
          <Typography variant="body2"><strong>Charger:</strong> {session.chargerName}</Typography>
          <Typography variant="body2"><strong>Site:</strong> {session.siteName}</Typography>
          <Typography variant="body2"><strong>Status:</strong> {session.status}</Typography>
          <Typography variant="body2"><strong>Energy Delivered:</strong> {session.energyDelivered?.toFixed(2) || '0.00'} kWh</Typography>
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel>Reason for Force Complete</InputLabel>
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            label="Reason for Force Complete"
            disabled={loading}
          >
            {FORCE_COMPLETE_REASONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedReason && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedReason.description}
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          rows={3}
          label={reason === 'other' ? 'Notes (Required)' : 'Additional Notes (Optional)'}
          placeholder="Provide additional context about why this session needs to be force-completed..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={loading}
          required={reason === 'other'}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleForceComplete}
          variant="contained"
          color="warning"
          disabled={loading || !reason}
          startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
        >
          {loading ? 'Processing...' : 'Force Complete Session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForceCompleteDialog;
